import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { PageEvent } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, firstValueFrom } from 'rxjs';
import { Company, CompanySite, Document, DocumentType, DocumentUsage } from '../model';
import { CompanyService } from '../common/service/company.service';
import { DocumentTypeService } from '../common/service/document-type.service';
import { DocumentService, PreviewDocument } from '../common/service/document.service';
import { Dialog } from '../common/dialog.service';
import { formatJsonDate } from '../util';
import { DocumentUploadedSortField } from './document-list-table.component';
import { ManagedDocument } from '../common/models/document.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { MatMenuModule } from '@angular/material/menu';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { RemoteDocumentService } from './remote-document.service';
import { ConfirmDeleteDialogComponent } from '../common/confirm-delete-dialog.component';
import { DocumentUploadService } from './document-upload.service';
import { PDFDocument } from 'pdf-lib';
import { clearDocumentLinkSidebarState, saveDocumentLinkSidebarState } from './document-link-context.util';
import { DocumentLinkUploadContextService } from './document-link-upload-context.service';
import { peekDocumentLinkFormState as peekDraftFormState } from './document-link-form-state.util';
import { ZipExplorerDialogComponent, ZipExplorerResult } from './zip-explorer-dialog.component';
import { hashBlob } from './document-hash.util';
import { CanvasStateService } from './canvas-state.service';
import { DocumentCanvasComponent } from './document-canvas.component';
import { AppState } from '../common';
import { CompanyWriteAccess } from '../common/service/company-write-access.service';
import { NotificationService } from '../common/service/notification.service';

type SortField = DocumentUploadedSortField;
type FilterState = { search: string; typeId: string; start: Date | null; end: Date | null };
type DocumentCategory = DocumentType['category'];
type CategoryGroup = { category: DocumentCategory; labelKey: string; types: DocumentType[] };

interface DialogData {
  company: Company;
  site: CompanySite;
  category?: DocumentCategory;
  originTitle?: string;
  linkedDocumentId?: string | null;
  formStateKey?: string | null;
  linkedArticleId?: string | null;
  restorePreviewDocumentId?: string | null;
  qualityWarning?: { resolutionBelowMinimum?: boolean; imageQualityBelowThreshold?: boolean } | null;
}

@Component({
  selector: 'app-document-link-dialog',
  templateUrl: 'document-link-dialog.component.html',
  styleUrl: 'document-link-dialog.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatPaginatorModule,
    MatTooltipModule,
    MatMenuModule,
    TranslateModule,
    NgxMatSelectSearchModule,
  ],
})
export class DocumentLinkDialogComponent implements OnInit, OnDestroy {
  /** Mirrors the server's `Company.mayWrite` — see `CompanyWriteAccess`. */
  readonly readOnly = inject(CompanyWriteAccess).readOnly;

  private readonly dialogRef = inject(MatDialogRef<DocumentLinkDialogComponent>);
  private readonly dialog = inject(MatDialog);
  private readonly dialogService = inject(Dialog);
  private readonly data = inject<DialogData>(MAT_DIALOG_DATA);
  private readonly companyService = inject(CompanyService);
  private readonly documentTypeService = inject(DocumentTypeService);
  private readonly documentService = inject(DocumentService);
  private readonly notification = inject(NotificationService);
  private readonly uploadService = inject(DocumentUploadService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly translate = inject(TranslateService);
  private readonly remoteService = inject(RemoteDocumentService);
  private readonly appState = inject(AppState);
  private readonly canvasState = inject(CanvasStateService);
  protected readonly enforcedCategory = signal<DocumentCategory | null>(null);
  private readonly linkUploadContext = inject(DocumentLinkUploadContextService);
  private readonly unloading = { value: false };

  readonly filters = this.fb.nonNullable.group({
    search: [''],
    typeId: [''],
    start: [null as Date | null],
    end: [null as Date | null],
  });

  readonly documents = signal<Document[]>([]);
  readonly previewMap = signal<Record<string, PreviewDocument>>({});
  readonly total = signal(0);
  readonly pageIndex = signal(0);
  readonly pageSize = signal(20);
  readonly sortField = signal<SortField>('date');
  readonly sortDirection = signal<'asc' | 'desc'>('desc');
  readonly selectedDocuments = signal<Map<string, Document>>(new Map());
  readonly selectedCount = computed(() => this.selectedDocuments().size);
  readonly groupsEnabled = signal(false);
  readonly groupedDocuments = computed(() => {
    const documents = new Map(this.documents().map((document) => [document.id, document]));
    return this.canvasState.groupsOnCanvas().map((group) => ({
      group,
      documents: (this.canvasState.groupedDocumentIds().get(group.id) ?? [])
        .map((documentId) => documents.get(documentId))
        .filter((document): document is Document => Boolean(document)),
    }));
  });
  readonly ungroupedDocuments = computed(() => {
    const groupedIds = new Set(
      Array.from(this.canvasState.groupedDocumentIds().values()).flatMap((groupIds) => groupIds)
    );
    return this.documents().filter((document) => !groupedIds.has(document.id));
  });
  readonly expandedUsageDocuments = signal<Set<string>>(new Set());
  private readonly filterState = signal<FilterState>(this.filters.getRawValue());
  private readonly documentTypes = signal<DocumentType[]>([]);
  protected readonly typesLoading = signal(true);
  private readonly typeSearchTerm = signal('');
  protected readonly typeSearchControl = this.fb.control('');
  protected readonly typeSearchActive = computed(() => !!this.typeSearchTerm());
  protected readonly typeGroups = computed(() =>
    this.buildCategoryGroups(this.filterTypes(this.documentTypes(), this.typeSearchTerm()))
  );
  private readonly allowedCategories = signal<DocumentCategory[] | null>(null);
  private readonly autoAttachId = this.data.linkedDocumentId ?? null;
  private restorePreviewDocumentId = this.data.restorePreviewDocumentId ?? null;
  private readonly pendingDocumentIds = signal<Set<string>>(new Set());
  private readonly closing = signal(false);
  private closeTimer: number | null = null;
  private autoAttachHandled = false;
  @ViewChild('uploadInput') uploadInput?: ElementRef<HTMLInputElement>;
  protected readonly filtersApplied = computed(() => {
    const filters = this.filterState();
    const enforced = this.enforcedCategory();
    const hasSearch = !!filters.search?.trim();
    let hasType = !!filters.typeId;
    if (enforced) {
      hasType = filters.typeId !== this.categoryFilterValue(enforced);
    }
    const hasDate = !!filters.start || !!filters.end;
    return hasSearch || hasType || hasDate;
  });
  constructor() {
    this.typeSearchControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.typeSearchTerm.set((value ?? '').toLowerCase().trim());
    });
    this.dialogRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        void this.closeWithAnimation();
      });
    this.dialogRef
      .keydownEvents()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          void this.closeWithAnimation();
        }
      });
  }

  ngOnInit(): void {
    window.addEventListener('beforeunload', this.markUnloading);
    this.canvasState.setStorageKey(this.canvasStorageKey());
    this.linkUploadContext.registerUploadHandler((files) => this.handleUploadFiles(files));
    this.refreshPendingDocumentIds();
    this.updateLinkingUrlState({ linkSidebar: 'open', documentId: this.autoAttachId });
    saveDocumentLinkSidebarState({
      linkSidebar: 'open',
      linkDialog: null,
      documentId: this.autoAttachId,
      category: this.data.category ?? null,
      linkedArticleId: this.data.linkedArticleId ?? null,
      originTitle: this.data.originTitle ?? null,
    });
    const initialCategory = (this.data.category as DocumentCategory | null) ?? null;
    if (initialCategory) {
      this.enforcedCategory.set(initialCategory);
      this.allowedCategories.set([initialCategory]);
    }
    this.resetFiltersToDefault(false);
    this.filters.valueChanges.pipe(debounceTime(250), takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.filterState.set({
        search: value.search ?? '',
        typeId: value.typeId ?? '',
        start: value.start ?? null,
        end: value.end ?? null,
      });
      void this.loadDocuments(0, this.pageSize());
    });
    void this.initialize();
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.markUnloading);
    this.linkUploadContext.registerUploadHandler(null);
    if (this.closeTimer !== null) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    if (!this.unloading.value) {
      this.updateLinkingUrlState({ linkSidebar: null, linkDialog: null, documentId: null });
      clearDocumentLinkSidebarState();
    }
  }

  protected async initialize() {
    await Promise.all([this.loadDocumentTypes(), this.loadDocuments()]);
  }

  protected async loadDocumentTypes() {
    this.typesLoading.set(true);
    try {
      const category = this.enforcedCategory();
      const types = await this.documentTypeService.listAll(category ?? undefined);
      this.documentTypes.set(types ?? []);
    } catch (error) {
      console.error('Failed to load document types', error);
      this.documentTypes.set([]);
    } finally {
      this.typesLoading.set(false);
    }
  }

  protected async loadDocuments(page = this.pageIndex(), size = this.pageSize()) {
    const companyId = this.data.company.id;
    if (!companyId) {
      this.documents.set([]);
      this.previewMap.set({});
      this.total.set(0);
      return;
    }

    const query: Record<string, any> = {
      page,
      size,
      sort: `${this.sortField()},${this.sortDirection()}`,
    };
    const { search, typeId, start, end } = this.filterState();
    const categoryFilter = this.extractCategoryFilter(typeId);
    const trimmedSearch = search?.trim();
    if (trimmedSearch) {
      query['search'] = trimmedSearch;
    }
    if (categoryFilter) {
      query['documentTypeCategory'] = categoryFilter;
    } else if (typeId) {
      query['documentType'] = typeId;
    } else if (this.enforcedCategory()) {
      query['documentTypeCategory'] = this.enforcedCategory();
    }
    if (start) {
      query['dateStart'] = formatJsonDate(start);
    }
    if (end) {
      query['dateEnd'] = formatJsonDate(end);
    }

    try {
      const pageData = await this.companyService.allDocumentsForCompanyTeam(companyId, query);
      const docs = pageData?.content ?? [];
      this.documents.set(docs);
      this.total.set(pageData?.totalElements ?? docs.length);
      this.pageIndex.set(pageData?.pageNumber ?? page);
      this.pageSize.set(pageData?.pageSize ?? size);
      await this.loadPreviews(docs);
      this.maybeAutoAttachDocument();
      this.maybeAutoPreviewDocument();
    } catch (error) {
      console.error('Failed to load company documents', error);
      this.documents.set([]);
      this.total.set(0);
      this.previewMap.set({});
    }
  }

  private canvasStorageKey() {
    const companyId = this.data.company?.id;
    const siteId = this.data.site?.id;
    return companyId && siteId ? `document-canvas:${companyId}:${siteId}` : null;
  }

  protected openUploadPicker(input: HTMLInputElement) {
    input.value = '';
    input.click();
  }

  protected async handleUploadSelection(event: Event) {
    const target = event.target as HTMLInputElement;
    const files = target.files ? Array.from(target.files) : [];
    target.value = '';
    await this.handleUploadFiles(files);
  }

  private async handleUploadFiles(files: File[]) {
    const supportedFiles = files.filter((file) => this.isSupportedUploadFile(file));
    const unsupportedFiles = files.filter((file) => !this.isSupportedUploadFile(file));
    for (const file of unsupportedFiles) {
      console.warn('Unsupported file type', file.name);
    }
    let uploaded = 0;
    let skipped = 0;
    for (const [index, file] of supportedFiles.entries()) {
      const queue = { position: index + 1, total: supportedFiles.length };
      const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (extension === 'zip') {
        (await this.handleZipUpload(file, queue)) ? uploaded++ : skipped++;
      } else {
        (await this.uploadAndReviewDocument(file, queue)) ? uploaded++ : skipped++;
      }
    }
    if (supportedFiles.length > 1) {
      await this.notification.success('Multi_file_upload_summary_tr', { uploaded, skipped });
    }
  }

  private isSupportedUploadFile(file: File) {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    return extension === 'pdf' || extension === 'zip' || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension);
  }

  private lookupMimeType(extension: string) {
    if (extension === 'pdf') {
      return 'application/pdf';
    }
    if (extension === 'jpg') {
      return 'image/jpeg';
    }
    if (['png', 'jpeg', 'gif', 'webp'].includes(extension)) {
      return `image/${extension}`;
    }
    return 'application/octet-stream';
  }

  private async countPdfPages(blob: Blob) {
    try {
      const pdf = await PDFDocument.load(await blob.arrayBuffer());
      return pdf.getPageCount();
    } catch {
      return 1;
    }
  }

  private async uploadAndReviewDocument(file: File, queue?: { position: number; total: number }): Promise<boolean> {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    const normalizedExtension = extension || (file.type === 'application/pdf' ? 'pdf' : 'png');
    const title = file.name.replace(/\.[^/.]+$/, '') || 'Document';
    const mimeType = file.type || this.lookupMimeType(normalizedExtension);
    const blob = file.slice(0, file.size, mimeType);
    const tempDocument: ManagedDocument = {
      id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2),
      title,
      mimeType,
      extension: normalizedExtension,
      kind: normalizedExtension === 'pdf' ? 'pdf' : 'image',
      blob,
      size: file.size,
      createdAt: new Date().toISOString(),
      pages: normalizedExtension === 'pdf' ? await this.countPdfPages(blob) : 1,
      source: 'upload',
    };
    const uploadHashes = await this.uploadService.buildUploadHashes(tempDocument);
    let suggestionAnalysisError = false;
    const documentTypeSuggestion = await this.analyzeUploadType(file, uploadHashes.fileHash).catch((error) => {
      console.warn('Failed to analyze uploaded document type', error);
      suggestionAnalysisError = true;
      return undefined;
    });
    const { DocumentWorkbenchDialogComponent } = await import('./document-workbench-dialog.component');
    const dialogData =
      normalizedExtension === 'pdf'
        ? ({
            mode: 'split',
            document: tempDocument,
            pdfDataUrl: await this.toDataUrl(blob, mimeType),
            maxPages: tempDocument.pages,
            documentTypeCategory: this.enforcedCategory() ?? null,
            allowSaveUpload: true,
            actionLabels: 'link',
            suggestedDocumentTypeId: documentTypeSuggestion?.documentTypeId ?? null,
            suggestedDocumentTypeName:
              documentTypeSuggestion?.documentType ?? documentTypeSuggestion?.documentTypeName ?? null,
            suggestedDocumentTypeConfidence: documentTypeSuggestion?.documentTypeConfidence ?? null,
            suggestionError: suggestionAnalysisError,
            suggestionErrorMessage: suggestionAnalysisError
              ? ('Please_choose_or_change_the_document_type_before_saving_tr' as const)
              : null,
            qualityWarning: documentTypeSuggestion?.qualityWarning ?? null,
            queuePosition: queue?.position,
            queueTotal: queue?.total,
          } as const)
        : ({
            mode: 'preview',
            document: tempDocument,
            documentTypeCategory: this.enforcedCategory() ?? null,
            allowSaveUpload: true,
            actionLabels: 'link',
            suggestedDocumentTypeId: documentTypeSuggestion?.documentTypeId ?? null,
            suggestedDocumentTypeName:
              documentTypeSuggestion?.documentType ?? documentTypeSuggestion?.documentTypeName ?? null,
            suggestedDocumentTypeConfidence: documentTypeSuggestion?.documentTypeConfidence ?? null,
            suggestionError: suggestionAnalysisError,
            suggestionErrorMessage: suggestionAnalysisError
              ? ('Please_choose_or_change_the_document_type_before_saving_tr' as const)
              : null,
            qualityWarning: documentTypeSuggestion?.qualityWarning ?? null,
            queuePosition: queue?.position,
            queueTotal: queue?.total,
          } as const);
    const result = await firstValueFrom(
      this.dialog
        .open(DocumentWorkbenchDialogComponent, {
          data: dialogData,
          height: '100dvh',
          maxHeight: '100dvh',
          width: '100vw',
          maxWidth: '100vw',
          panelClass: 'document-workbench-dialog',
        })
        .afterClosed()
    );
    if (!result) {
      return false;
    }

    if (result.kind === 'split') {
      return await this.uploadSplitPdfResult(tempDocument, result);
    }

    if (result.kind !== 'metadata' || result.action !== 'save-upload') {
      return false;
    }

    try {
      const uploaded = await this.documentService.uploadWithDuplicateConfirm(
        file,
        result.metadata.documentTypeId!,
        result.metadata.title,
        () => {},
        uploadHashes
      );
      if (!uploaded) {
        return false;
      }
      const formattedDate = result.metadata.documentDate ? result.metadata.documentDate.toISOString() : undefined;
      if (formattedDate) {
        await this.documentService.update(uploaded.id, { ...uploaded, date: formattedDate } as Document);
      }
      await this.loadDocuments(this.pageIndex(), this.pageSize());
      this.selectUploadedDocument(uploaded.id);
      return true;
    } catch (error) {
      console.error('Failed to upload document from linking dialog', error);
      throw error;
    }
  }

  private async uploadSplitPdfResult(
    sourceDocument: ManagedDocument,
    result: {
      title: string;
      pages: number[];
      metadata: { documentTypeId?: string; documentDate?: Date; title: string };
      action?: string;
    }
  ): Promise<boolean> {
    const sourcePdf = await PDFDocument.load(await sourceDocument.blob!.arrayBuffer());
    const splitPdf = await PDFDocument.create();
    const copiedPages = await splitPdf.copyPages(
      sourcePdf,
      result.pages.map((page) => page - 1)
    );
    copiedPages.forEach((page) => splitPdf.addPage(page));
    const bytes = await splitPdf.save();
    const blob = new Blob([bytes.slice()], { type: 'application/pdf' });
    const title = result.title?.trim() || sourceDocument.title;
    const splitDocument: ManagedDocument = {
      id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2),
      title,
      mimeType: 'application/pdf',
      extension: 'pdf',
      kind: 'pdf',
      blob,
      size: bytes.byteLength,
      createdAt: new Date().toISOString(),
      pages: copiedPages.length,
      source: 'pdf-split',
      documentTypeId: result.metadata.documentTypeId ?? undefined,
      documentDate: result.metadata.documentDate ? result.metadata.documentDate.toISOString() : undefined,
    };
    const uploadHashes = await this.uploadService.buildUploadHashes(splitDocument);
    const file = new File([blob], `${title}.pdf`, { type: 'application/pdf' });
    const uploaded = await this.documentService.uploadWithDuplicateConfirm(
      file,
      result.metadata.documentTypeId!,
      title,
      () => {},
      uploadHashes
    );
    if (!uploaded) {
      return false;
    }
    const formattedDate = result.metadata.documentDate ? result.metadata.documentDate.toISOString() : undefined;
    if (formattedDate) {
      await this.documentService.update(uploaded.id, { ...uploaded, date: formattedDate } as Document);
    }
    await this.loadDocuments(this.pageIndex(), this.pageSize());
    this.selectUploadedDocument(uploaded.id);
    return true;
  }

  private async handleZipUpload(file: File, queue?: { position: number; total: number }): Promise<boolean> {
    const dialogRef = this.dialog.open(ZipExplorerDialogComponent, {
      data: {
        file,
        mode: 'link',
        documentTypeCategory: this.enforcedCategory() ?? null,
        queuePosition: queue?.position,
        queueTotal: queue?.total,
      },
      width: '100vw',
      maxWidth: '100vw',
      height: '100dvh',
      maxHeight: '100dvh',
      panelClass: 'zip-explorer-dialog',
    });
    const results = await firstValueFrom(dialogRef.afterClosed());
    if (!results?.length) {
      return false;
    }
    for (const result of results) {
      await this.uploadZipEntry(result);
    }
    await this.loadDocuments(this.pageIndex(), this.pageSize());
    return true;
  }

  private async uploadZipEntry(result: ZipExplorerResult) {
    if (!result.documentTypeId) {
      return;
    }
    const blob = new Blob([result.data.slice()], { type: result.mimeType });
    const title = result.title?.trim() || 'Document';
    const tempDocument: ManagedDocument = {
      id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2),
      title,
      mimeType: result.mimeType,
      extension: result.extension,
      kind: result.extension === 'pdf' ? 'pdf' : 'image',
      blob,
      size: result.size,
      createdAt: new Date().toISOString(),
      pages: result.extension === 'pdf' ? await this.countPdfPages(blob) : 1,
      source: 'zip-entry',
      documentTypeId: result.documentTypeId ?? undefined,
      documentTypeName: result.documentTypeName ?? undefined,
      documentDate: result.documentDate ? result.documentDate.toISOString() : new Date().toISOString(),
    };
    const uploadHashes = await this.uploadService.buildUploadHashes(tempDocument);
    const file = new File([blob], `${title}.${result.extension}`, { type: result.mimeType });
    const uploaded = await this.documentService.uploadWithDuplicateConfirm(
      file,
      result.documentTypeId,
      title,
      () => {},
      uploadHashes
    );
    if (!uploaded) {
      return;
    }
    const formattedDate = tempDocument.documentDate ?? undefined;
    if (formattedDate) {
      await this.documentService.update(uploaded.id, { ...uploaded, date: formattedDate } as Document);
    }
    await this.loadDocuments(this.pageIndex(), this.pageSize());
    this.selectUploadedDocument(uploaded.id);
  }

  private async analyzeUploadType(file: File, fileHash: string) {
    const response = await this.documentService.analyzeDocument(file, fileHash);
    if (!response?.analysis) {
      return undefined;
    }
    const analysis = response.analysis as typeof response.analysis & {
      quality?: {
        resolution?: { belowMinimum?: boolean | null };
        imageQuality?: { belowThreshold?: boolean | null };
      } | null;
    };
    return {
      documentType: analysis.documentType ?? null,
      documentTypeId: analysis.documentTypeId ?? null,
      documentTypeConfidence: analysis.documentTypeConfidence ?? null,
      documentTypeName: analysis.documentType ?? null,
      qualityWarning:
        analysis.quality?.imageQuality?.belowThreshold && analysis.quality?.resolution?.belowMinimum
          ? {
              resolutionBelowMinimum: true,
              imageQualityBelowThreshold: true,
            }
          : null,
    };
  }

  private async toDataUrl(blob: Blob, mimeType: string) {
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(new Blob([blob], { type: mimeType }));
    });
  }

  private selectUploadedDocument(documentId: string) {
    this.groupsEnabled.set(false);
    const doc = this.documents().find((item) => item.id === documentId);
    if (!doc) {
      return;
    }
    this.selectedDocuments.update((current) => {
      const next = new Map(current);
      next.set(doc.id, doc);
      return next;
    });
  }

  protected async loadPreviews(documents: Document[]) {
    const ids = documents.map((doc) => doc.id).filter((id): id is string => Boolean(id));
    if (!ids.length) {
      this.previewMap.set({});
      return;
    }
    try {
      this.previewMap.set(await this.documentService.previewList(ids));
    } catch (error) {
      console.warn('Failed to load document previews', error);
      this.previewMap.set({});
    }
  }

  protected handlePageChange(event: PageEvent) {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
    void this.loadDocuments(event.pageIndex, event.pageSize);
  }

  protected changeSort(field: SortField) {
    if (this.sortField() === field) {
      this.sortDirection.set(this.sortDirection() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortField.set(field);
      this.sortDirection.set(field === 'title' ? 'asc' : 'desc');
    }
    void this.loadDocuments(0, this.pageSize());
  }

  protected handleTableRowSelect(row: Document | ManagedDocument) {
    if (this.isServerDocument(row)) {
      this.selectDocument(row);
    }
  }

  protected handleTableRowView(row: Document | ManagedDocument) {
    if (this.isServerDocument(row)) {
      void this.viewDocument(row);
    }
  }

  protected documentUsages(document: Document) {
    return document.usages ?? [];
  }

  protected usageCount(document: Document) {
    return this.documentUsages(document).length;
  }

  protected usageCountLabel(document: Document) {
    const count = this.usageCount(document);
    return count === 1 ? 'usage_tr' : 'usages_tr';
  }

  protected isUsageExpanded(document: Document) {
    return this.expandedUsageDocuments().has(document.id);
  }

  protected toggleUsageList(document: Document, event: Event) {
    event.stopPropagation();
    this.expandedUsageDocuments.update((current) => {
      const next = new Set(current);
      if (next.has(document.id)) {
        next.delete(document.id);
      } else {
        next.add(document.id);
      }
      return next;
    });
  }

  protected selectDocument(document: Document) {
    this.selectedDocuments.update((current) => {
      const next = new Map(current);
      if (next.has(document.id)) {
        next.delete(document.id);
      } else {
        next.set(document.id, document);
      }
      return next;
    });
  }

  protected isGroupFullySelected(documents: Document[]) {
    return documents.length > 0 && documents.every((document) => this.isSelected(document));
  }

  protected isGroupPartiallySelected(documents: Document[]) {
    const selected = documents.filter((document) => this.isSelected(document)).length;
    return selected > 0 && selected < documents.length;
  }

  protected toggleGroup(documents: Document[], checked: boolean) {
    this.selectedDocuments.update((current) => {
      const next = new Map(current);
      for (const document of documents) {
        if (checked) {
          next.set(document.id, document);
        } else {
          next.delete(document.id);
        }
      }
      return next;
    });
  }

  protected clearFilters() {
    this.resetFiltersToDefault(true);
  }

  protected async viewDocument(document: Document) {
    try {
      this.updateLinkingUrlState({ linkDialog: 'preview', documentId: document.id });
      const response = await this.documentService.view(document.id, undefined, {
        name: document.title ?? document.fileName ?? document.id,
      });
      if (!response?.body) {
        return;
      }
      const { body } = response;
      await this.dialogService.showPdf(document.title ?? 'Document', body, {
        fileName: document.fileName ?? `${document.title ?? 'document'}.pdf`,
        mimeType: document.fileType ?? body.type,
      });
    } catch (error) {
      console.error('Failed to view document', error);
    } finally {
      this.updateLinkingUrlState({ linkDialog: null, documentId: null });
    }
  }

  protected async editDocument(document: Document, event: Event) {
    event.stopPropagation();
    const updated = await this.remoteService.editUploadedDocument(document, {
      enforcedCategory: this.enforcedCategory(),
    });
    if (updated) {
      await this.loadDocuments(this.pageIndex(), this.pageSize());
    }
  }

  protected async deleteDocument(document: Document, event: Event) {
    event.stopPropagation();
    const confirmed = await this.confirmDocumentDeletion(document.title);
    if (!confirmed) {
      return;
    }
    await this.remoteService.deleteUploadedDocument(document);
    this.selectedDocuments.update((current) => {
      const next = new Map(current);
      next.delete(document.id);
      return next;
    });
    await this.loadDocuments(this.pageIndex(), this.pageSize());
  }

  protected closeDialog() {
    void this.closeWithAnimation();
  }

  protected showCanvas() {
    const activeTypeId = this.filterState().typeId || null;
    const categoryFilter = this.extractCategoryFilter(activeTypeId);
    const canvasRef = this.dialog.open(DocumentCanvasComponent, {
      data: {
        documents: this.documents(),
        previewMap: this.previewMap(),
        storageKey: this.canvasStorageKey(),
        selectionMode: true,
        selectedDocumentIds: Array.from(this.selectedDocuments().keys()),
        category: categoryFilter ?? this.enforcedCategory(),
        documentTypeId: categoryFilter ? null : activeTypeId,
      },
      width: '100vw',
      height: '100dvh',
      maxWidth: '100vw',
      maxHeight: '100dvh',
      panelClass: 'document-canvas-dialog',
      autoFocus: false,
      restoreFocus: false,
    });
    canvasRef.afterClosed().subscribe((result?: { selectedDocumentIds?: string[] }) => {
      const selectedIds = result?.selectedDocumentIds ?? [];
      const documentsById = new Map(this.documents().map((document) => [document.id, document]));
      this.selectedDocuments.set(
        new Map(
          selectedIds
            .map((documentId) => [documentId, documentsById.get(documentId)])
            .filter((entry): entry is [string, Document] => Boolean(entry[1]))
        )
      );
      void this.loadDocuments(this.pageIndex(), this.pageSize());
    });
  }

  protected confirm() {
    const selected = Array.from(this.selectedDocuments().values());
    if (!selected.length) {
      return;
    }
    void this.closeWithAnimation(selected);
  }

  protected isSelected(document: Document) {
    return this.selectedDocuments().has(document.id);
  }

  protected previewSrc(document: Document) {
    const preview = this.previewMap()[document.id]?.preview;
    return preview ? `data:image/png;base64,${preview}` : null;
  }

  protected hasPreviewError(document: Document) {
    return !!this.previewMap()[document.id]?.error_type;
  }

  protected hasUsages(document: Document) {
    return this.documentUsages(document).length > 0;
  }

  protected usageIcon(usage: DocumentUsage): string {
    switch (usage.type) {
      case 'chain':
      case 'chain_pending':
        return 'link';
      case 'product_attribute':
      case 'product_element':
        return 'inventory_2';
      case 'report':
      case 'report_detail_product':
      case 'report_detail_article':
      case 'report_detail_article_note':
      case 'report_product_notes':
      case 'attribute_report_notes':
        return 'assessment';
      case 'company':
        return 'apartment';
      case 'site':
      case 'site_manual':
        return 'forest';
      default:
        return 'link';
    }
  }

  protected usageLabel(usage: DocumentUsage): string {
    const label = usage.label ?? '';
    switch (usage.type) {
      case 'chain':
      case 'chain_pending':
        return label ? `Chain: ${label}` : 'Chain';
      case 'product_attribute':
        return label ? `Attribute: ${label}` : 'Attribute';
      case 'product_element':
        return label ? `Packaging element: ${label}` : 'Packaging element';
      case 'report':
      case 'report_detail_product':
      case 'report_detail_article':
      case 'report_detail_article_note':
      case 'report_product_notes':
      case 'attribute_report_notes':
        return label ? `Report: ${label}` : 'Report';
      case 'company':
        return label ? `Company: ${label}` : 'Company';
      case 'site':
      case 'site_manual':
        return label ? `Site: ${label}` : 'Site';
      default:
        return label || usage.type;
    }
  }

  protected handleUsageClick(usage: DocumentUsage, event: Event) {
    event.stopPropagation();
    const cs = this.appState.companySite$.value;
    if (!cs?.company) return;
    const companyId = cs.company.id;
    const siteId = cs.site?.id ?? cs.company.sites?.[0]?.id;
    if (!siteId) return;
    window.open(`/company/${companyId}/site/${siteId}/${usage.link}`, '_blank', 'noopener');
  }

  protected isPendingDocument(document: Document) {
    return this.pendingDocumentIds().has(document.id);
  }

  private async confirmDocumentDeletion(name?: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        name: name ?? '',
        type: '',
      },
    });
    return firstValueFrom(dialogRef.afterClosed());
  }

  private closeWithAnimation(result?: Document[]) {
    if (this.closing()) {
      clearDocumentLinkSidebarState();
      return Promise.resolve();
    }
    this.closing.set(true);
    this.dialogRef.addPanelClass('document-link-sidebar-panel-closing');
    return new Promise<void>((resolve) => {
      this.closeTimer = window.setTimeout(() => {
        this.closeTimer = null;
        this.dialogRef.close(result);
        clearDocumentLinkSidebarState();
        resolve();
      }, 180);
    });
  }

  private refreshPendingDocumentIds() {
    const formStateKey = this.data.formStateKey ?? null;
    if (!formStateKey) {
      this.pendingDocumentIds.set(new Set());
      return;
    }
    const savedState = peekDraftFormState<any>(formStateKey);
    this.pendingDocumentIds.set(new Set(this.extractDocumentIds(savedState)));
  }

  private extractDocumentIds(value: unknown): string[] {
    const ids = new Set<string>();
    const visit = (node: unknown) => {
      if (!node) {
        return;
      }
      if (Array.isArray(node)) {
        node.forEach(visit);
        return;
      }
      if (typeof node !== 'object') {
        return;
      }
      const record = node as Record<string, unknown>;
      const id = record['id'];
      if (typeof id === 'string' && id.trim()) {
        ids.add(id);
      }
      for (const key of [
        'documents',
        'noteDocuments',
        'manualDocuments',
        'documentIds',
        'manualDocumentIds',
        'attributeDraft',
      ]) {
        if (key in record) {
          visit(record[key]);
        }
      }
    };
    visit(value);
    return Array.from(ids);
  }

  private updateLinkingUrlState(state: {
    linkSidebar?: string | null;
    linkDialog?: string | null;
    documentId?: string | null;
  }) {
    try {
      const currentUrl = this.location.path(true);
      const hashIndex = currentUrl.indexOf('#');
      const baseWithQuery = hashIndex === -1 ? currentUrl : currentUrl.slice(0, hashIndex);
      const hash = hashIndex === -1 ? '' : currentUrl.slice(hashIndex);
      const [basePath, queryString = ''] = baseWithQuery.split('?', 2);
      const searchParams = new URLSearchParams(queryString);
      for (const [key, value] of Object.entries(state)) {
        const queryKey = key === 'documentId' ? 'linkId' : key;
        if (value === null || value === undefined || value === '') {
          searchParams.delete(queryKey);
        } else {
          searchParams.set(queryKey, value);
        }
      }
      const shouldPersistContext = (searchParams.get('linkSidebar') as string | null) === 'open';
      if (shouldPersistContext) {
        if (this.data.category) {
          searchParams.set('linkCategory', this.data.category);
        } else {
          searchParams.delete('linkCategory');
        }
        if (this.data.linkedArticleId) {
          searchParams.set('linkArticleId', this.data.linkedArticleId);
        } else {
          searchParams.delete('linkArticleId');
        }
      } else {
        searchParams.delete('linkCategory');
        searchParams.delete('linkArticleId');
      }
      const nextQuery = searchParams.toString();
      const nextUrl = `${basePath}${nextQuery ? `?${nextQuery}` : ''}${hash}`;
      this.location.replaceState(nextUrl);
      if (state.linkSidebar === null && state.linkDialog === null && state.documentId === null) {
        clearDocumentLinkSidebarState();
        return;
      }
      saveDocumentLinkSidebarState({
        linkSidebar: (searchParams.get('linkSidebar') as 'open' | null | undefined) ?? null,
        linkDialog: (searchParams.get('linkDialog') as 'preview' | null | undefined) ?? null,
        documentId: (searchParams.get('linkId') as string | null | undefined) ?? null,
        category: this.data.category ?? null,
        linkedArticleId: this.data.linkedArticleId ?? null,
        originTitle: this.data.originTitle ?? null,
      });
    } catch {
      // ignore url update failures
    }
  }

  private readonly markUnloading = () => {
    this.unloading.value = true;
  };

  protected categoryFilterValue(category: DocumentCategory) {
    return `category:${category}`;
  }

  private buildCategoryGroups(types: DocumentType[]): CategoryGroup[] {
    const grouped = new Map<DocumentCategory, DocumentType[]>();
    const allowed = this.allowedCategories();
    types.forEach((type) => {
      const category = (type.category ?? 'company') as DocumentCategory;
      if (allowed && !allowed.includes(category)) {
        return;
      }
      const bucket = grouped.get(category);
      if (bucket) {
        bucket.push(type);
      } else {
        grouped.set(category, [type]);
      }
    });
    return Array.from(grouped.entries())
      .map(([category, list]) => ({
        category,
        labelKey: this.categoryLabelKey(category),
        types: [...list].sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.category.localeCompare(b.category));
  }

  private categoryLabelKey(category: DocumentCategory) {
    switch (category) {
      case 'chain':
        return 'chain_tr';
      case 'report':
        return 'Reports_tr';
      case 'attributes':
        return 'product_attributes_tr';
      default:
        return 'Company_tr';
    }
  }

  private filterTypes(types: DocumentType[], term: string) {
    const normalized = term.trim().toLowerCase();
    if (!normalized) {
      return types;
    }
    return types.filter((type) => type.name.toLowerCase().includes(normalized));
  }

  private extractCategoryFilter(value: string | null | undefined): DocumentCategory | null {
    if (!value?.startsWith('category:')) {
      return null;
    }
    const suffix = value.slice('category:'.length) as DocumentCategory;
    if (['company', 'chain', 'report', 'attributes'].includes(suffix)) {
      return suffix as DocumentCategory;
    }
    return null;
  }

  private resetFiltersToDefault(triggerLoad: boolean) {
    const defaults = this.buildDefaultFilterState();
    this.filters.reset(defaults, { emitEvent: false });
    this.filterState.set(defaults);
    if (triggerLoad) {
      void this.loadDocuments(0, this.pageSize());
    }
  }

  private buildDefaultFilterState(): FilterState {
    const enforced = this.enforcedCategory();
    return {
      search: '',
      typeId: enforced ? this.categoryFilterValue(enforced) : '',
      start: null,
      end: null,
    };
  }

  private maybeAutoAttachDocument() {
    if (this.autoAttachHandled || this.restorePreviewDocumentId || !this.autoAttachId) {
      return;
    }
    const doc = this.documents().find((document) => document.id === this.autoAttachId);
    if (!doc) {
      return;
    }
    this.autoAttachHandled = true;
    this.selectedDocuments.set(new Map([[doc.id, doc]]));
    setTimeout(() => this.confirm());
  }

  private maybeAutoPreviewDocument() {
    if (!this.restorePreviewDocumentId) {
      return;
    }
    const doc = this.documents().find((document) => document.id === this.restorePreviewDocumentId);
    if (!doc) {
      return;
    }
    this.restorePreviewDocumentId = null;
    void this.viewDocument(doc);
  }

  private isServerDocument(row: Document | ManagedDocument): row is Document {
    const managed = row as ManagedDocument;
    return managed.dataUrl === undefined && managed.blob === undefined;
  }
}
